/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/img-redundant-alt */
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  IMAGE_COMPRESSION,
  IMAGE_TYPE,
  INVOICE_STATUS_TYPE,
  PROJECT_STATUS_COLOR,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  STATUS_TYPE,
  TASK_PRIORITY,
  audioExtensions,
  imageExtensions,
  videoExtensions
} from '../global/constants';
import {
  CategorizedFiles,
  CommentFilesInterface,
  LinkObject,
  MyTaskDetailsInterface,
  RawUpdateCommentInterface
} from '../interfaces/TaskInterface';
import { uploadFileToFirebase, uploadFilesToFirebase, deleteFileFromFirebase } from './firebaseHelper';
import DraftImage from '../assets/images/Draft.png';
import OverDueImage from '../assets/images/Over_due.png';
import PartiallyPaidImage from '../assets/images/Partially_Paid.png';
import PendingImage from '../assets/images/Pending.png';
import PaidImage from '../assets/images/Paid.png';
import UserPreferenceSingleton from './userPreferenceSingleton';
import { nanoid } from 'nanoid';
import { Printscreen } from '../component/renderHtml/styles';
import { captureException } from '../services/logService';
import groupBy from 'lodash/groupBy';
import { CustomWindow } from '../interfaces/OneSignalInterface';
import { RenderImagePreview } from '../component/renderImagePreview';
import { ProjectModel } from '../mobx/models/project';
import {
  SelectRangeInterface,
  WorkloadReportApiResponseInterface,
  WorkloadReportMemberInterface,
  WorkloadReportDateInterface,
  WorkloadWeeklyScheduleInterface
} from '../interfaces/ReportInterface';
import { rootStore } from '../mobx/rootStore';
import { RawComment } from '../mobx/interfaces/comment';
import { UserModel } from '../mobx/models/user';
import { RawFile } from '../mobx/interfaces/task';
import { randomColorsArray } from '../global/row';
import router from '../routes/routes';
import Image from '../component/image';
import { logout } from '../services/authServices';

declare const window: CustomWindow;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/**
 * Converts file to base64
 * @param file File object
 * @returns Promise<{ profile: string | ArrayBuffer | null, file: File }>
 */
const convertBase64Image = (file: File): Promise<{ profile: string | ArrayBuffer | null; file: File }> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      resolve({
        profile: fileReader.result,
        file
      });
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export interface ImageCompressionOptions {
  /** Max output width in pixels. Aspect ratio is preserved. Default 2000. */
  maxWidth?: number;
  /** Max output height in pixels. Aspect ratio is preserved. Default unconstrained. */
  maxHeight?: number;
  /** Encoder quality between 0 and 1 for lossy formats. Default 0.85. */
  quality?: number;
  /** Hard byte cap; quality then dimensions are reduced until the output fits (e.g. 50KB avatars). */
  maxSizeBytes?: number;
  /** Output mime type. WebP keeps transparency. Default 'image/webp'. */
  mimeType?: 'image/webp' | 'image/jpeg';
}

/** Reads a File into a base64 data URL string. */
const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/** Loads a data URL into an HTMLImageElement. */
const loadImageElement = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    // Note: the module-level `Image` import is a React component, so build the element directly.
    const img = document.createElement('img');
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = dataUrl;
  });

/** Promisified canvas.toBlob. */
const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });

// Draws the image at the given size and encodes it, lowering quality to fit a byte cap.
const renderImageToBlob = async (
  img: HTMLImageElement,
  width: number,
  height: number,
  mimeType: string,
  quality: number,
  maxSizeBytes?: number
): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);

  let currentQuality = quality;
  let blob = await canvasToBlob(canvas, mimeType, currentQuality);
  while (blob && maxSizeBytes && blob.size > maxSizeBytes && currentQuality > IMAGE_COMPRESSION.MIN_QUALITY) {
    // Clamp to MIN_QUALITY so we never encode below the floor.
    currentQuality = Math.max(IMAGE_COMPRESSION.MIN_QUALITY, Math.round((currentQuality - 0.1) * 100) / 100);
    blob = await canvasToBlob(canvas, mimeType, currentQuality);
  }
  return blob;
};

// Resizes & compresses an image before upload, keeping aspect ratio.
// Returns the original unchanged for svg/gif or if compression can't help.
export const compressImageFile = async (file: File, options?: ImageCompressionOptions): Promise<File> => {
  const {
    maxWidth = IMAGE_COMPRESSION.DEFAULT_MAX_WIDTH,
    maxHeight = Infinity,
    quality = IMAGE_COMPRESSION.DEFAULT_QUALITY,
    maxSizeBytes,
    mimeType = 'image/webp'
  } = options || {};

  // Only raster images that can be safely drawn to a canvas. Skip vectors/animations.
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
    return file;
  }

  try {
    const dataUrl = await readFileAsDataUrl(file);
    const img = await loadImageElement(dataUrl);

    // Scale down only (never upscale) while keeping aspect ratio.
    const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);

    // No byte cap and no resize needed: keep the original file untouched (truly lossless, no re-encode).
    if (!maxSizeBytes && scale >= 1) {
      return file;
    }

    let width = Math.max(1, Math.round(img.width * scale));
    let height = Math.max(1, Math.round(img.height * scale));

    let blob = await renderImageToBlob(img, width, height, mimeType, quality, maxSizeBytes);

    // If still over the byte cap, shrink the size and try again until it fits.
    while (
      blob &&
      maxSizeBytes &&
      blob.size > maxSizeBytes &&
      Math.max(width, height) > IMAGE_COMPRESSION.MIN_DIMENSION
    ) {
      width = Math.max(IMAGE_COMPRESSION.MIN_DIMENSION, Math.round(width * 0.85));
      height = Math.max(IMAGE_COMPRESSION.MIN_DIMENSION, Math.round(height * 0.85));
      blob = await renderImageToBlob(img, width, height, mimeType, quality, maxSizeBytes);
    }

    if (!blob) return file;

    const newExtension = mimeType === 'image/webp' ? 'webp' : 'jpg';
    const baseName = file.name.replace(/\.[^/.]+$/, '') || 'image';
    const compressedFile = new File([blob], `${baseName}.${newExtension}`, { type: blob.type || mimeType });

    // With a byte cap, always use the resized result (the original would break a limit).
    if (maxSizeBytes) return compressedFile;

    // No cap: keep the original if compression didn't make it smaller.
    if (compressedFile.size >= file.size) return file;

    return compressedFile;
  } catch (error) {
    captureException(error);
    return file; // Fail open: never block an upload because compression failed.
  }
};

/**
 * Handles file upload & validation
 * @param event Input change event
 * @param options Optional client-side compression constraints applied before returning
 * @returns Promise<{ profile: string | ArrayBuffer | null, file: File } | null>
 */
export const handleFileReadImage = async (
  event: React.ChangeEvent<HTMLInputElement>,
  options?: ImageCompressionOptions
): Promise<{ profile: string | ArrayBuffer | null; file: File } | null> => {
  try {
    const file = event.target.files?.[0];
    if (!file) return null;
    const fileExtension = file.name?.split('.').pop()?.toLowerCase();
    if (
      fileExtension === 'jpg' ||
      fileExtension === 'jpeg' ||
      fileExtension === 'png' ||
      fileExtension === 'webp'
    ) {
      // Resize/compress on the client before upload instead of rejecting large files.
      const compressedFile = await compressImageFile(file, options);
      return await convertBase64Image(compressedFile);
    } else {
      rootStore.alertNotificationStore.setErrorMessage('Please upload a valid type photo.');
      return null;
    }
  } catch (error) {
    captureException(error);
    return null;
  }
};

/**
 * @desc Checks if given value is Number
 * @param {*} value // Accepts string
 */
export function isNumber(value: any) {
  const myRegEx = /^\s*\d+(\s+\d+)*\s*$/;
  const isValid = myRegEx.test(value);
  return isValid ? true : false;
}

/**
 * @desc Checks for valid email
 * @param {*} value // Accepts string
 */
export function isEmail(value: any) {
  // eslint-disable-next-line max-len
  const myRegEx =
    // eslint-disable-next-line max-len
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = myRegEx.test(value);
  return isValid ? true : false;
}

/**
 * @desc Checks for Empty string
 * @param {*} value // Accepts string, object
 */
export function isEmpty(value: any) {
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  ) {
    return true;
  } else {
    return false;
  }
}

export const getUniqueId = () => {
  function S4() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  return (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase();
};

export const isElectronApp = () => {
  // return true;
  if (navigator.userAgent) {
    return navigator.userAgent.toLowerCase().includes('electron');
  }
  return false;
};

export const isMAC = () => {
  // return false;
  if (navigator.userAgent) {
    return navigator.userAgent.toLowerCase().includes('mac') || navigator.userAgent.toLowerCase().includes('macintosh');
  }
  return false;
};

export const isWindows = () => {
  // return true;
  if (navigator.userAgent) {
    return navigator.userAgent.toLowerCase().includes('windows');
  }
  return false;
};

export const isReactNative = () => {
  return window.isRNWebView;
};

export const isIOS = () => {
  return isReactNative() && window.platformOs === 'ios';
};

export const isIphone = () => {
  return isIOS() && !window.isIpad;
};

export const isIpad = () => {
  return isIOS() && window.isIpad;
};

export const isAndroid = () => {
  return isReactNative() && window.platformOs === 'android';
};

export const getCookie = (cname: string) => {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    if (!isEmpty(c)) {
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c && c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
  }
  return '';
};

export const isFloatOrNumber = (value: string) => {
  // Regular expression to check if the value is a float or number without alphabets
  const regex = /^[+-]?\d*(\.\d*)?$/;

  return regex.test(value);
};

export const groupByProject = (tasks: MyTaskDetailsInterface[]) => {
  const groupedMap = tasks.reduce((acc, task) => {
    if (!acc.has(task.projectId)) {
      acc.set(task.projectId, {
        project_id: task.projectId,
        tasks: [],
        title: task.projectName || ''
      });
    }
    acc.get(task.projectId)!.tasks.push(task);
    return acc;
  }, new Map<string, { project_id: string; tasks: MyTaskDetailsInterface[]; title: string }>());
  return Array.from(groupedMap.values());
};

/**
 * @desc: to group task by status
 */
export const groupByStatus = (collection: any, iteratee: string) => {
  const groupResult = groupBy(collection, iteratee);
  return Object.keys(groupResult).map((key) => {
    const sortedData = groupResult[key].toSorted((a, b) => a.Index - b.Index);
    return { Type: Number(key), data: sortedData };
  });
};

/**
 * @desc check does it dev mode or live mode
 * it return false only if its a production build
 */
export const isDev = () => {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return false;
};

/**
 * @desc get query param by name
 */
export const getUrlParam = (query: any, name: any) => {
  let queryParams = new URLSearchParams();
  if (query) queryParams = new URLSearchParams(query);
  return queryParams.get(name);
};

export function mathRound(number: any, digit = 2) {
  try {
    if (Number(number) < 1) digit = 3;
    if (number) return Number(number).toFixed(digit);
  } catch (e) {
    captureException(e);
  }
  return Number(0).toFixed(2);
}

/**
 * @desc get formatted date
 */
export const getFormattedDate = (date: any) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return (day < 10 ? `0${day}` : day) + '/' + (month < 10 ? `0${month}` : month) + '/' + year;
  // return day + '/' + month + '/' + year;
};

export const getAPIErrorReason = (e: any) => {
  if (e) {
    if (e?.response?.status === 401) {
      logout(true);
    }
    if (e.response?.data) {
      return e.response.data.reason || e.response.data.message || e.response.data.error;
    } else if (e.message) {
      return e.message;
    }
  }
};

export const currencyWithDecimal = (num: any) => {
  let returnValue = num;
  try {
    let digit = 2;
    if (num) {
      if (Number(num) < 1) digit = 3;
      if (Number(num) > 999) digit = 1;
      const num2 = Number(num).toFixed(digit);
      returnValue = num2;
    } else {
      returnValue = Number(0).toFixed(digit);
    }
  } catch (e) {
    captureException(e);
  }
  return returnValue;
};

export const getDateString = (date: any) => {
  //return yyyyMMdd
  if (date) {
    date = new Date(date);
    return date.getFullYear() + ('0' + (date.getMonth() + 1))?.slice(-2) + ('0' + date.getDate())?.slice(-2);
  }
  return;
};

export const getUTCDate = (date: any) => {
  if (date) date = new Date(date);
  else date = new Date();
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

export const formatSecondsDuration = (seconds: number, format: 'colon' | 'label'): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (format === 'colon') {
    const hoursPart = hours.toString();
    const minutesPart = minutes.toString().padStart(2, '0');
    const secondsPart = secs.toString().padStart(2, '0');
    return `${hoursPart}:${minutesPart}:${secondsPart}`;
  }

  let timeString = '';
  if (hours > 0) {
    timeString += `${hours.toString().padStart(2, '0')}h `;
  }
  timeString += `${minutes.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
  return timeString;
};

export const calculateDay = (createdDate: any) => {
  const date = new Date();
  const oldDate = new Date(createdDate);
  const Difference_In_Time = date.getTime() - oldDate.getTime();
  const Difference_In_Days: any = Difference_In_Time / (1000 * 3600 * 24);
  if (Difference_In_Days < 1) return 'Today';
  else if (Difference_In_Days < 7) return `${Math.floor(Difference_In_Days)} days ago`;
  else if (Difference_In_Days < 30 && Math.floor(Difference_In_Days / 7) === 1) return `Week ago`;
  else if (Difference_In_Days < 30 && Math.floor(Difference_In_Days / 7) !== 1)
    return `${Math.floor(Difference_In_Days / 7)} Weeks ago`;
  const months = date.getMonth() - oldDate.getMonth() + 12 * (date.getFullYear() - oldDate.getFullYear());
  if (months < 12) return `${months} Months ago`;
  return `${Math.floor(months / 12)} Years ago`;
};

export const formatDateWithTime = (date: string | Date) => {
  return dayjs(date).format('ddd, DD MMM YYYY, h:mm A');
};

export const calculateDueTime = (inputDueDate: any) => {
  const currentDate = dayjs(getUTCDate(new Date()).toISOString());
  const dueDate = dayjs(getUTCDate(inputDueDate).toISOString());
  const diffInDays = dueDate.diff(currentDate, 'days');
  const absoluteDiffInDays = Math.abs(diffInDays);
  let result, dueParam;
  if (diffInDays === 0) {
    result = 'Today';
    dueParam = 'Today';
  } else if (diffInDays === 1) {
    result = 'Tomorrow';
    dueParam = 'Tomorrow';
  } else if (diffInDays === -1) {
    result = 'Yesterday';
    dueParam = `Yesterday`;
  } else if (diffInDays > 1 && diffInDays <= 7) {
    result = `${diffInDays}d`;
    dueParam = `in ${diffInDays} days`;
  } else if (diffInDays < -1 && diffInDays >= -7) {
    result = `${absoluteDiffInDays}d`;
    dueParam = `${absoluteDiffInDays} days ago`;
  } else if (diffInDays > 7 && diffInDays <= 30) {
    const weeks = Math.floor(diffInDays / 7);
    result = `${weeks}w`;
    dueParam = `in ${weeks} week${weeks > 1 ? 's' : ''}`;
  } else if (diffInDays < -7 && diffInDays >= -30) {
    const weeksLeft = Math.floor(absoluteDiffInDays / 7);
    result = `${weeksLeft}w`;
    dueParam = `${weeksLeft} week${weeksLeft > 1 ? 's' : ''} ago`;
  } else if (diffInDays > 30 && diffInDays <= 365) {
    const months = Math.floor(diffInDays / 30);
    result = `${months}mo`;
    dueParam = `in ${months} month${months > 1 ? 's' : ''}`;
  } else if (diffInDays < -30 && diffInDays >= -365) {
    const monthsLeft = Math.floor(absoluteDiffInDays / 30);
    result = `${monthsLeft}mo`;
    dueParam = `${monthsLeft} month${monthsLeft > 1 ? 's' : ''} ago`;
  } else if (diffInDays > 365) {
    const years = Math.floor(diffInDays / 365);
    result = `${years}y`;
    dueParam = `in ${years} year${years > 1 ? 's' : ''}`;
  } else if (diffInDays < -365) {
    const yearsLeft = Math.floor(absoluteDiffInDays / 365);
    result = `${yearsLeft}y`;
    dueParam = `${yearsLeft} year${yearsLeft > 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(diffInDays / 30);
    result = `${months}mo`;
  }
  return { dueTime: result, overDue: diffInDays < 0, dueParam };
};

export const getDayFromDate = (dateString: string): string => {
  const date = new Date(dateString); // Convert the string to a Date object
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
};

export const convertToUTCISO = (dateString: string, timeString: string) => {
  // Parse the date and time in IST
  const tzDate = dayjs.tz(`${dateString} ${timeString}`, 'DD/MM/YYYY HH:mm', 'Asia/Kolkata');

  // Convert to UTC and format as ISO
  return tzDate.utc().toISOString();
};

export const formatToDateString = (isoDateString: string) => {
  const date = new Date(isoDateString);
  return !isEmpty(isoDateString) ? date.toLocaleDateString('en-GB') : ''; // 'en-GB' format gives "DD/MM/YYYY"
};

export const floatToHHMM = (num: number) => {
  let hours = Math.floor(num);
  let minutes = Math.round((num - hours) * 60);
  return `${hours > 9 ? hours : `0${hours}`} : ${minutes > 9 ? minutes : `0${minutes}`}`;
};

export const floatToHHMMSS = (num: number) => {
  let hours = Math.floor(num);
  let minutes = Math.floor((num - hours) * 60);
  let seconds = Math.round((num - hours) * 3600 - minutes * 60);
  let timeString = '';
  if (hours > 0) {
    timeString += `${hours > 9 ? hours : `0${hours}`}h `;
  }
  timeString += `${minutes > 9 ? minutes : `0${minutes}`}m ${seconds > 9 ? seconds : `0${seconds}`}s`;
  return timeString;
};

export const getFloatTimeValue = (time: string): number => {
  if (isEmpty(time)) return 0;
  const timeRef = time?.split(':');
  const floatValue = Number(timeRef[0]) + Number(timeRef[1]) / 60;
  return floatValue;
};

export const getDateObjecttoDDMMM = (dateInput: any) => {
  let date = new Date(dateInput);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const monthIndex = date.getMonth();
  return `${day} ${monthNames[monthIndex]}`;
};

export const getDateObjecttoMMMDDYYYY = (dateInput: any) => {
  let date = new Date(dateInput);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const day = date.getDate();
  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  //current Date
  const currentDate = new Date();
  const curentDay = currentDate.getDate();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  if (day === curentDay && year === currentYear && monthIndex === currentMonthIndex) return 'Today';
  return `${monthNames[monthIndex]} ${day}, ${year}`;
};

// Get Date Formate By month

export const getDateByThisMonth = () => {
  const startOfMonth = dayjs().startOf('month').format('YYYY-MM-DD');
  const endOfMonth = dayjs().endOf('month').format('YYYY-MM-DD');
  return `${startOfMonth}/${endOfMonth}`;
};

export const checkIsOverdue = (inputDateRef: Date) => {
  try {
    const inputDate = new Date(inputDateRef).getTime();
    const currentDate = new Date().getTime();
    const diffTime = Math.abs(inputDate - currentDate);
    if (diffTime >= 0) return false;
    else return true;
  } catch (e) {
    captureException(e);
  }
};

export const htmlToPlainText = (html: string) => {
  // Create a temporary DOM element
  let tempElement = document.createElement('div');

  // Set the HTML content of the temporary element
  tempElement.innerHTML = html;

  // Extract and return the text content
  return tempElement.textContent || tempElement.innerText || '';
};

export const getFileContent = (htmlSnippet: string) => {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlSnippet;
  const linkElement = tempElement.querySelectorAll('a');
  let content: any = tempElement.querySelectorAll('p');
  const extractedLinks: LinkObject[] = [];

  if (!content) {
    content = document.createElement('p');
    content.innerHTML = tempElement.innerHTML;
  }

  let newDesc: string = '';
  content.forEach((item: any) => {
    if (item?.getAttribute('class') !== 'p') newDesc = newDesc + item?.innerHTML;
  });

  linkElement.forEach((linkElement) => {
    const href = linkElement.getAttribute('href');
    if (href?.startsWith('https://firebasestorage') || href?.startsWith('/projects/details')) {
      const size = linkElement.getAttribute('id');
      const name = linkElement.getAttribute('name') || '';
      const parts = name?.split('.');
      const extension = parts[parts.length - 1];

      extractedLinks.push({
        href: href || '',
        name: name.replace(/\s+/g, '-'),
        size: size || '0 MB',
        file_type: extension
      });
    }
  });
  return { content: !isEmpty(newDesc) ? `<p>${newDesc}</p>` : '', uploadedFiles: extractedLinks };
};

export const generateRandomFilename = () => {
  let lastTimestamp = 0;
  let timestamp = Date.now();
  if (timestamp <= lastTimestamp) {
    timestamp = lastTimestamp + 1;
  }
  lastTimestamp = timestamp;
  return timestamp;
};

export const getFirebaseUrlFromFile = async (imageData: any, folderName: string, fileName?: string) => {
  return uploadFileToFirebase(imageData, folderName, fileName);
};

/** Default storage sub-folder (under `workspaces/{workspaceId}/`) for uploads. */
export const DEFAULT_UPLOAD_FOLDER = 'files/';

// Folder for a task comment's files: `projects/{projectId}/tasks/{taskId}/comments/`, or `files/` if ids are missing.
export const buildCommentUploadFolder = (projectId?: string, taskId?: string): string =>
  projectId && taskId ? `projects/${projectId}/tasks/${taskId}/comments/` : DEFAULT_UPLOAD_FOLDER;

// Folder for a chat message's files: `messages/{groupId}/`, or `files/` if the group id is missing.
export const buildMessageUploadFolder = (groupId?: string): string =>
  groupId ? `messages/${groupId}/` : DEFAULT_UPLOAD_FOLDER;

// Folder for a task's attachment files: `projects/{projectId}/tasks/attachments/`, or `files/` if the project id is missing.
export const buildTaskAttachmentFolder = (projectId?: string): string =>
  projectId ? `projects/${projectId}/tasks/attachments/` : DEFAULT_UPLOAD_FOLDER;

// Folder for a task description's inline images: `projects/{projectId}/tasks/description-images/`, or `files/` if the project id is missing.
export const buildTaskDescriptionImageFolder = (projectId?: string): string =>
  projectId ? `projects/${projectId}/tasks/description-images/` : DEFAULT_UPLOAD_FOLDER;

export const removeFileFromFirebase = async (fileUrl: string) => {
  return deleteFileFromFirebase(fileUrl);
};

export const getFirebaseUrlsFromFiles = async (fileData: { file: File }[], folderName: string): Promise<string[]> => {
  return uploadFilesToFirebase(fileData, folderName);
};

export const convertBase64ToFile = async (base64String: any, isScreenRecording?: boolean) => {
  if (base64String?.startsWith('blob:')) {
    base64String = await convertBlobUrlToDataUrl(base64String);
  }
  if (isEmpty(base64String)) return;
  const cleanedBase64String = base64String?.replace(/^data:(.*?;base64,)?/, '');
  // eslint-disable-next-line no-useless-escape
  let contentType = base64String?.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-+.]+)?;base64,(.+)$/)?.[1];
  const extension = contentType?.split('/')[1];
  const fileName = isScreenRecording ? `Screen_recording_${nanoid(8)}.mp4` : `file.${extension}`;

  try {
    const decodedString = atob(cleanedBase64String);
    const byteNumbers = new Array(decodedString.length);
    for (let i = 0; i < decodedString.length; i++) {
      byteNumbers[i] = decodedString.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    const file = new File([blob], fileName, { type: contentType });
    return file;
  } catch (error) {
    captureException(error);
  }
};

export const getInvoiceIcon = (type: number) => {
  switch (type) {
    case INVOICE_STATUS_TYPE.DRAFT:
      return DraftImage;
    case INVOICE_STATUS_TYPE.OVER_DUE:
      return OverDueImage;
    case INVOICE_STATUS_TYPE.PENDING:
      return PendingImage;
    case INVOICE_STATUS_TYPE.PARTIALLY_PAID:
      return PartiallyPaidImage;
    case INVOICE_STATUS_TYPE.PAID:
      return PaidImage;
    default:
      return;
  }
};

export const getIsDarkMode = () => {
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark;
};

export const calculateEndTime = (startTime: string, totalHours: number): string => {
  const startDate = new Date(startTime);
  const endDate = new Date(startDate.getTime() + totalHours * 60 * 60 * 1000);

  // Format the date as a string in the same format as the input
  const endTimeString = endDate.toISOString();

  return endTimeString;
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  // Format time as "7:58 pm"
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });

  // Format date as "Sep 29"
  const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return { time, formattedDate };
};

export const countDays = (dateString: string) => {
  const currentDate = dayjs();
  const targetDate = dayjs(dateString);
  const daysDifference = targetDate.diff(currentDate, 'days') + 1;
  return daysDifference;
};

export const fetchAndConvertToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read image as data URL.'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Failed to read image as data URL.'));
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log('Failed to fetch or convert to base64:', error);
    throw error;
  }
};

export const convertBlobUrlToDataUrl = (blobUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!blobUrl.startsWith('blob')) {
      // If it's not a Blob URL, resolve immediately with the original URL
      resolve(blobUrl);
    } else {
      // Create a new XMLHttpRequest
      const xhr = new XMLHttpRequest();
      // Set the responseType to 'blob'
      xhr.responseType = 'blob';
      // Define the onload callback function
      xhr.onload = function () {
        if (xhr.status === 200) {
          // Get the recovered Blob
          const recoveredBlob = xhr.response;

          // Create a new FileReader
          const reader = new FileReader();

          // Define the onload callback function for the FileReader
          reader.onload = function () {
            // Get the Blob as a data URL
            const blobAsDataUrl = reader.result as string;
            // Use the Blob as a regular URL
            resolve(blobAsDataUrl);
          };
          // Read the recovered Blob as a data URL
          reader.readAsDataURL(recoveredBlob);
        } else {
          // Reject the Promise if the request fails
          reject(new Error(`Failed to fetch Blob: ${xhr.status}`));
        }
      };
      // Open the XMLHttpRequest with the Blob URL
      xhr.open('GET', blobUrl);
      // Send the XMLHttpRequest
      xhr.send();
    }
  });
};

export const extensionName = (item: any) => {
  const filename = item?.name;
  const parts = filename?.split('.');
  const extension = parts?.length > 1 ? parts[parts?.length - 1] : '';
  const limitedExtension = extension.length > 5 ? extension.substring(0, 4) : extension;
  return `.${isEmpty(limitedExtension) && !isEmpty(item?.file_type) ? item?.file_type : limitedExtension}`;
};

export const extensionResource = (item: any) => {
  const filename = item?.Label;
  const parts = filename?.split('.');
  const extension = parts?.length > 1 ? parts[parts?.length - 1] : '';
  return `.${isEmpty(extension) && !isEmpty(item?.FileType) ? item?.FileType : extension}`;
};

export const renderPrntImageFile = (imageUrl: string, imageName: string) => {
  return (
    <>
      <Printscreen>
        <div className='prnt-screen-box'>
          <div className='prnt-screen-top'>
            <div className='prnt-screen-header'>
              <Image imageKey='prntscrn_icon' alt='LightShot Image' width='20px' height='20px' />
              <div className='prnt-screen-text'>
                <a href={imageUrl} target='_blank' rel='noreferrer' className='prnt-url-link'>
                  {imageUrl}
                </a>
              </div>
            </div>
          </div>
          <div className='figma-image-container'>
            <RenderImagePreview imageUrl={imageName} altText={'prnt image'} type={IMAGE_TYPE.PRINT_SCREEN} />
          </div>
        </div>
      </Printscreen>
    </>
  );
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const categorizeFiles = (files: CommentFilesInterface[]) => {
  const categories: CategorizedFiles = {
    imageFiles: [],
    videoFiles: [],
    audioFiles: [],
    otherFiles: []
  };

  files?.forEach((file) => {
    const ext = extensionName(file).toLowerCase();

    if (imageExtensions.includes(ext)) {
      categories.imageFiles.push(file);
    } else if (videoExtensions.includes(ext)) {
      categories.videoFiles.push(file);
    } else if (audioExtensions.includes(ext)) {
      categories.audioFiles.push(file);
    } else {
      categories.otherFiles.push(file);
    }
  });

  return categories;
};

export const getFirebaseUniqueUserId = () => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  const workspace = UserPreferenceSingleton.getInstance().getWorkspace();
  if (userDetails?.id && workspace?.id) {
    const uniqueUserId = workspace?.id + '_' + userDetails?.id;
    return { uniqueUserId, userId: userDetails?.id, workspaceId: workspace?.id };
  }
  return { uniqueUserId: '', userId: userDetails?.id, workspaceId: workspace?.id };
};

export const getCurrentDomain = () => {
  return window.location.hostname;
};

export const getColorWithOpacity = (color: string) => {
  const rgbValues = color.match(/[0-9a-f]{2}/gi);
  if (rgbValues && rgbValues.length === 3) {
    // Convert RGB values to rgba format with opacity 0.9
    return `rgba(${parseInt(rgbValues[0], 16)}, ${parseInt(rgbValues[1], 16)}, ${parseInt(rgbValues[2], 16)}, 0.9)`;
  } else {
    // If the color format is not supported or invalid, return null or original color
    return null;
  }
};

export const isMediaRecordingSupported = () => {
  return navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';
};

export const calculateColor = (status: number) => {
  if (status >= 1 && status <= 4) return PROJECT_STATUS_COLOR[status];
  else return 'transparent'; // Default color
};

export const getMonthRange = (startDate: Date | string, endDate: Date | string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const months = [];

  let current = new Date(start);
  current.setDate(1); // Set to the first day of the month

  while (current <= end) {
    const month = (current.getMonth() + 1).toString().padStart(2, '0');
    const year = current.getFullYear();
    months.push(`${month}-${year}`);
    current.setMonth(current.getMonth() + 1);
  }

  return months;
};

/**
 * Checks if a phone number is valid.
 *
 * @param phoneNumber - The phone number string to validate.
 * @returns A boolean indicating whether the phone number is valid.
 */
export const isPhoneNumberValid = (phoneNumber: string): boolean => {
  // Remove spaces, dashes, and parentheses
  const cleanedNumber = phoneNumber.replace(/\D/g, '');

  // Define a regular expression for exactly 10 digits (no extra characters allowed)
  const phoneRegex = /^\d{10}$/;

  // Check if the cleaned number matches the pattern
  return phoneRegex.test(cleanedNumber);
};

const convertBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      const item = {
        fileContent: fileReader.result,
        fileType: file?.type,
        name: file?.name,
        size: file?.size,
        file: file
      };
      resolve(item);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const handleFileRead = async (file: File) => {
  if (file?.size > 2.5e8) {
    dispatchErrorMessage('Please upload a file smaller than 250 MB.');
    return false;
  }
  const newPromise = convertBase64(file);
  try {
    const value = await Promise.all([newPromise]);
    return value[0];
  } catch (error) {
    captureException(error);
    return null;
  }
};

export const handleFileUpload = async (
  uploadedFiles: RawFile[],
  fileArray: File[],
  ref?: any,
  isVideoAsFile?: boolean,
  folderName = 'files/'
) => {
  // Validation: Remove duplicate files based on name and size
  const existingFileNames = new Set(uploadedFiles?.map((file) => file?.name));
  const uniqueFiles = fileArray?.filter((file) => !existingFileNames.has(file?.name));

  if (uniqueFiles?.length === 0) {
    dispatchErrorMessage('All selected files are already uploaded.');
    return;
  }

  // Handle Files with `handleFileRead` for further processing
  const processedFiles = await Promise.all(
    uniqueFiles?.map(async (file) => {
      const base64 = await handleFileRead(file);
      return base64 ? file : null; // Exclude invalid files
    })
  );

  const validFiles: File[] = processedFiles?.filter((file): file is File => !!file);

  if (validFiles?.length === 0) {
    dispatchErrorMessage('No valid files to upload.');
    return;
  }

  // Map validFiles to the required format [{ file: File }]
  const fileData = validFiles?.map((file) => ({ file }));

  const isImageFile = (file: { file: File }) =>
    ['image/jpeg', 'image/jpg', 'image/png'].includes(file?.file?.type);
  // Images are embedded inline only when an editor ref exists. Without a ref
  // (e.g. project resource upload) treat images as regular files so they upload.
  const imageFiles = ref ? fileData?.filter(isImageFile) : [];
  const otherFiles = ref ? fileData?.filter((file) => !isImageFile(file)) : fileData;

  // Upload files and update task input
  const uploadedUrls = await getFirebaseUrlsFromFiles(otherFiles, folderName);
  ref?.current?.addImageFile(imageFiles?.map((fileItem) => fileItem?.file));

  // Process Uploaded File
  const updatedFiles = uploadedUrls
    ?.map((url: string, index: number) => {
      const file = validFiles[index];
      const sizeInMB = file?.size / (1024 * 1024);
      const sizeValue = sizeInMB >= 1 ? `${sizeInMB.toFixed(2)} MB` : `${(file?.size / 1024).toFixed(2)} KB`;

      if (['video/mp4', 'video/mov', 'video/mkv', 'video/webm'].includes(file?.type) && !isVideoAsFile && ref) {
        ref?.current?.addRecordingCard(url, file?.name);
      } else {
        return {
          name: file?.name,
          href: url,
          size: sizeValue,
          file_type: file?.type?.split('/')[1]
        };
      }
      return null; // No object to add for inline content types
    })
    .filter((fileObj) => fileObj !== null); // Remove null entries
  return updatedFiles;
};

export const isValidUrl = (url: string) => {
  // Only treat it as a URL if it is one word (no spaces) starting with http/https.
  // This stops text like "Title: ..." from being wrongly detected as a link.
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed === '' || /\s/.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

export const isEmptyMessage = (htmlString: string): boolean => {
  const strippedContent = htmlString.replace(/<\/?[^>]+(>|$)/g, '').trim();
  const hasImage = /<img\b[^>]*>/i.test(htmlString);
  const hasVideo = /<video\b[^>]*>/i.test(htmlString);
  const hasEmbedContent = /<div\b[^>]*\burl="[^"]*"[^>]*>/i.test(htmlString); // Catches screen recordings, YouTube embeds, etc.
  const hasImageUploadEmbed = /<div[^>]*data-type="image-upload-embed"/i.test(htmlString); // Check for image upload embeds
  return strippedContent.length === 0 && !hasImage && !hasVideo && !hasEmbedContent && !hasImageUploadEmbed;
};

const STATUS_ICON_MAP: Record<number, string> = {
  [STATUS_TYPE.BACKLOG]: 'task-status-icon',
  [STATUS_TYPE.UNSTARTED]: 'task-todo-icon',
  [STATUS_TYPE.IN_PROGRESS]: 'task-inprogress-icon',
  [STATUS_TYPE.IN_REVIEW]: 'task-inreview-icon',
  [STATUS_TYPE.COMPLETED]: 'task-completed-icon',
  [STATUS_TYPE.CANCELLED]: 'task-cancelled-icon'
};
export const renderStatusIconName = (type: number) => STATUS_ICON_MAP[type] ?? 'task-todo-icon';

const PRIORITY_ICON_MAP: Record<number, string> = {
  [TASK_PRIORITY.NO_PRIORITY]: 'Priority-No-priority-icon',
  [TASK_PRIORITY.URGENT]: 'Priority-Urgent-icon',
  [TASK_PRIORITY.HIGH]: 'Priority-High-icon',
  [TASK_PRIORITY.MEDIUM]: 'Priority-Medium-icon',
  [TASK_PRIORITY.LOW]: 'Priority-low-icon'
};
export const renderPriorityIconName = (type: number) => PRIORITY_ICON_MAP[type] ?? 'Priority-No-priority-icon';

// Get data object based on label
export function getDateRange(label: string): SelectRangeInterface {
  let startDate: Dayjs;
  let endDate: Dayjs;

  switch (label) {
    case 'Today':
      startDate = dayjs().startOf('day');
      endDate = dayjs().startOf('day');
      break;
    case 'Yesterday':
      startDate = dayjs().subtract(1, 'day').startOf('day');
      endDate = dayjs().subtract(1, 'day').startOf('day');
      break;
    case 'Last 7 Days':
      startDate = dayjs().startOf('week');
      endDate = dayjs();
      break;
    case 'Last 30 Days':
      startDate = dayjs().subtract(29, 'days').startOf('day');
      endDate = dayjs();
      break;
    case 'This Week':
      startDate = dayjs().startOf('week');
      endDate = dayjs();
      break;
    case 'Last Week':
      startDate = dayjs().subtract(1, 'week').startOf('week');
      endDate = dayjs().subtract(1, 'week').endOf('week');
      break;
    case 'This Month':
      startDate = dayjs().startOf('month');
      endDate = dayjs();
      break;
    case 'Last Month':
      startDate = dayjs().subtract(1, 'month').startOf('month');
      endDate = dayjs().subtract(1, 'month').endOf('month');
      break;
    default:
      startDate = dayjs().startOf('day');
      endDate = dayjs().startOf('day');
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    key: 'selection'
  };
}

export const getObjectFromArray = (array: any[], key: string) =>
  array.reduce((obj, item) => {
    obj[item[key]] = item;
    return obj;
  }, {});

const handleDefaultGrouping = (currentProject: ProjectModel) => {
  if (currentProject?.statusEnable) {
    return SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS;
  } else if (currentProject?.isGroupEnabled) {
    return SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP;
  }
  return SHOW_TASK_GROUPING_TYPE.NO_GROUPING;
};

export const updateTaskFilterGrouping = (currentProject: ProjectModel) => {
  const { taskFilterType, statusEnable, isGroupEnabled, isMilestone } = currentProject || {};
  const groupingStatus = taskFilterType?.groupingStatus;

  // Return default grouping if no grouping status or it's explicitly set to DEFAULT
  if ((groupingStatus && groupingStatus < 0) || groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT) {
    return handleDefaultGrouping(currentProject);
  }

  // Check if current grouping type is valid based on project settings
  const isInvalidGrouping =
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS && !statusEnable) ||
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP && !isGroupEnabled) ||
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE && !isMilestone);

  // Return default grouping if current grouping is invalid
  if (isInvalidGrouping) {
    return handleDefaultGrouping(currentProject);
  }
  return groupingStatus;
};

export const updateProjectFilterGrouping = (currentProject: ProjectModel) => {
  const { projectFilterType, statusEnable, isGroupEnabled, isMilestone } = currentProject || {};
  const groupingStatus = projectFilterType?.groupingStatus;

  // Return default grouping if no grouping status or it's explicitly set to DEFAULT
  if ((groupingStatus && groupingStatus < 0) || groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT) {
    return handleDefaultGrouping(currentProject);
  }

  // Check if current grouping type is valid based on project settings
  const isInvalidGrouping =
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS && !statusEnable) ||
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP && !isGroupEnabled) ||
    (groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE && !isMilestone);

  // Return default grouping if current grouping is invalid
  if (isInvalidGrouping) {
    return handleDefaultGrouping(currentProject);
  }
  return groupingStatus;
};

export const updateTaskFilterOrder = (currentProject: ProjectModel) => {
  const { orderByStatus } = currentProject?.taskFilterType || {};
  return orderByStatus || SHOW_TASK_BY_ORDER.CREATED_DATE;
};

export const updateProjectFilterOrder = (currentProject: ProjectModel) => {
  const { orderByStatus } = currentProject?.projectFilterType || {};
  return orderByStatus || SHOW_TASK_BY_ORDER.CREATED_DATE;
};
export const getCookieValue = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

export const getHoursFromTime = (time: Date) => {
  const date1 = new Date(time).getTime();
  const date2 = new Date().getTime();
  const timeDifference = date2 - date1; // Difference in milliseconds
  return timeDifference / (1000 * 60 * 60);
};

export const getRawComment = (newComment: RawUpdateCommentInterface, currentUser: UserModel): RawComment => {
  const commentDetail = {
    id: newComment?.id || '',
    createdOn: newComment?.createdOn || new Date().toISOString(),
    createdBy: currentUser?.id,
    updatedBy: currentUser?.id,
    updatedTime: new Date().toISOString(),
    isArchived: false
  };
  return { ...(newComment || {}), ...(commentDetail || {}) };
};

function dispatchErrorMessage(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

//function to check if all box is checked so list type will be any
export const checkIsAllBoxChecked = (list: any) => {
  const checkTick = list.filter((item: any) => item.checked);
  return list.length === checkTick.length;
};

const getChar = (value: string): string => value.charAt(0).toUpperCase();

export const getAvatarName = (name: string, avatarSingleLetter?: boolean): string => {
  if (!name?.trim()) return '#';
  const splitName = name.trim()?.split(' ');
  if (splitName.length === 1 || splitName[1].trim() === '' || avatarSingleLetter) {
    return getChar(splitName[0]);
  }
  return getChar(splitName[0]) + getChar(splitName[1]);
};

const getAsciiChar = (name: string): number => {
  return getChar(name).charCodeAt(0);
};

export const getColorIndex = (name: string): number => {
  const asciiValue = getAsciiChar(name);
  return (asciiValue - 64) % (randomColorsArray.length - 1);
};

/**
 * Transform Workload Report API data to component format
 * @param apiData - API array for workload
 * @param workspaceUsers - workspace users list
 * @param startDate - report start date (YYYY-MM-DD)
 * @param endDate - report end date (YYYY-MM-DD)
 * @param regularHoursPerDay - working hours per day (default = 8)
 * @returns WorkloadReportMemberInterface[]
 */
export const transformWorkloadReportData = (
  apiData: WorkloadReportApiResponseInterface[],
  workspaceUsers: UserModel[],
  startDate: string,
  endDate: string,
  regularHoursPerDay: number = 8
): WorkloadReportMemberInterface[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const allDates: string[] = [];
  let currentDate = start.clone();
  while (currentDate.isBefore(end) || currentDate.isSame(end)) {
    allDates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }
  return apiData.map((item) => {
    const user = workspaceUsers.find((user) => user.id === item.userId);
    const userName = user?.name || 'Unknown User';
    const avatarUrl = user?.profile_photo || user?.avatar?.avatar || '';

    const totalWorkingHours = item.totalWorkingHours || 0;
    const totalOvertimeHours = item.totalOvertimeHours || 0;
    const totalHours = item.totalHours || totalWorkingHours + totalOvertimeHours;
    const totalWorkingHoursFormatted = formatHours(totalWorkingHours);
    const totalOvertimeHoursFormatted = formatHours(totalOvertimeHours);
    const totalHoursFormatted = formatHours(totalHours);

    const dateMap = new Map<string, WorkloadReportDateInterface>(
      item.dates.map((dates: WorkloadReportDateInterface) => [dates.date, dates])
    );
    // Build weekly schedule (prioritize dateData for weekends)
    const weeklySchedule: WorkloadWeeklyScheduleInterface[] = allDates.map((date) => {
      const dateMoment = dayjs(date);
      const dayOfWeek = dateMoment.day();
      const dayName = dateMoment.format('ddd');
      const dayNumber = dateMoment.date();
      const dateData = dateMap.get(date);

      if (dateData) {
        const { workingHours, overTimeHours } = dateData;
        const totalDayHours = workingHours + overTimeHours;
        const hoursFormatted = totalDayHours > 0 ? formatHours(totalDayHours) : '';
        let type: 'completed' | 'overtime';
        let color: 'green' | 'red';
        if (overTimeHours > 0 || totalDayHours > regularHoursPerDay) {
          type = 'overtime';
          color = 'red';
        } else {
          type = 'completed';
          color = 'green';
        }
        return {
          date,
          day: dayName,
          dayNumber,
          type,
          hours: hoursFormatted,
          statusIcon: type === 'completed' ? ('checkmark' as const) : undefined,
          color
        };
      } else if (dayOfWeek === 0 || dayOfWeek === 6) {
        return {
          date,
          day: dayName,
          dayNumber,
          type: 'weekend' as const,
          hours: '',
          color: 'grey' as const,
          label: 'Weekend'
        };
      } else {
        return {
          date,
          day: dayName,
          dayNumber,
          type: 'planned' as const,
          hours: '',
          color: 'brown' as const
        };
      }
    });
    return {
      id: item.userId,
      name: userName,
      avatarUrl,
      totalHours: totalHoursFormatted,
      totalWorkingHours: totalWorkingHoursFormatted,
      overloadTime: totalOvertimeHoursFormatted,
      tasksCount: item.taskCount,
      projectsCount: item.projectCount,
      weeklySchedule,
      dates: item.dates,
      totalOvertimeHours
    };
  });
};

// Helper for "xh Ym" format
function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const excludeTabsRoutes = () => [
  ...router.filter((route) => route && !route.private).map((route) => route!.path),
  '/select-workspace',
  '/onboarding',
  '/membership'
];

export const excludedTabBar = (pathname: string) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  return excludeTabsRoutes().some((x) => !userDetails?.id || pathname?.includes(x));
};

export const serializeNode = (node: any): string => {
  if (!node) return '';

  if (node.type === 'text') return node.data || '';

  if (node.type === 'tag') {
    const attrs = node.attribs
      ? ' ' +
        Object.entries(node.attribs)
          .map(([k, v]) => `${k}="${v}"`)
          .join(' ')
      : '';
    const childrenHtml = (node.children || []).map(serializeNode).join('');
    return `<${node.name}${attrs}>${childrenHtml}</${node.name}>`;
  }

  return '';
};

// Function to get tomorrow's date at 9 AM
export const getTomorrowAt9am = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
};

// Function to get ISO string for the same time next week
export const getNextWeekISO = (): string => dayjs().add(1, 'week').toISOString();

// Function to check if reschedule time has expired (i.e., is in the past)
export const isRescheduleExpired = (time: string): boolean => {
  if (isEmpty(time)) return true;
  const ms = new Date(time).getTime();
  return isNaN(ms) ? false : ms <= Date.now();
};
